import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, ListChecks, Calculator, ChartLineUp, Bell, Question } from "@phosphor-icons/react";

function Step({ n, title, body, icon: Icon, color = "#007AFF" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-white/10 rounded-sm p-5 hover:border-white/25 transition-colors"
      data-testid={`gs-step-${n}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center font-display text-2xl" style={{ background: `${color}22`, color }}>
            {n}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <Icon size={16} weight="fill" style={{ color }} />
            <h3 className="font-display text-xl tracking-wider">{title}</h3>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
        </div>
      </div>
    </motion.div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 group" data-testid={`gs-faq-${q.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}>
      <summary className="cursor-pointer font-bold text-sm flex items-center gap-2">
        <Question size={14} weight="bold" className="text-[#22D3EE]" /> {q}
      </summary>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed pl-6">{a}</p>
    </details>
  );
}

export default function GettingStarted() {
  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="getting-started-page">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-1 flex items-center gap-2">
          <Compass size={14} weight="fill" /> Primer · 5 min read
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">HOW TO FOLLOW THESE PREDICTIONS</h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          New here? Skim this once. Every morning we publish 1-3 MLB predictions (and WNBA when in season) — here&apos;s exactly how to use them, how the math works, and what to do if you&apos;re not ready to bet at full size.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <Step
          n={1}
          title="Check the morning prediction(s)"
          body="Each day before first pitch we lock 1-3 predictions into a tamper-evident ledger (you can audit it at /receipts). Picks come from two analysts — Charlie hunts bounce-backs, Dakota rides momentum. We attribute each prediction to one of them."
          icon={ListChecks}
          color="#007AFF"
        />
        <Step
          n={2}
          title="Size your position on Optimizer"
          body="Don&apos;t follow at our default $250 unit — that&apos;s our internal benchmark. Tell the Optimizer your target weekly winnings + bankroll and it builds your personalized 3-tier ladder. The number you see on each prediction card auto-scales to YOUR size."
          icon={Calculator}
          color="#22D3EE"
        />
        <Step
          n={3}
          title="Place the position at the moneyline shown"
          body="We publish the live DraftKings moneyline on every prediction. Use that line or any line within a couple of points — wide swings change the math. Our prediction is to side with the team on their next scheduled game."
          icon={Bell}
          color="#A855F7"
        />
        <Step
          n={4}
          title="Track your prediction P&L on /account"
          body="Once you&apos;ve placed picks, see your personalized win-rate + dollar P&L on the Account page. Numbers update automatically as games settle. No spreadsheet needed."
          icon={ChartLineUp}
          color="#22C55E"
        />
      </div>

      <h2 className="font-display text-3xl tracking-wider mb-4 text-zinc-200">Two analysts — what they do</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <div className="bg-[#121212] border border-[#007AFF]/40 rounded-sm p-5">
          <div className="text-[#007AFF] font-bold text-xs uppercase tracking-widest mb-2">Charlie · Chop Trend</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Hunts teams stuck in a chop pattern — losses sandwiched between wins. When a team posts an <strong>isolated single-game loss</strong> (W → L → W setup), Charlie watches for the next time that pattern repeats inside the team&apos;s loss-cycle. The 2nd, 3rd, 5th, 7th, and 8th isolated losses in a cycle are the highest-conviction triggers.
          </p>
        </div>
        <div className="bg-[#121212] border border-[#A855F7]/40 rounded-sm p-5">
          <div className="text-[#A855F7] font-bold text-xs uppercase tracking-widest mb-2">Dakota · Win-Continuation</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Catches the rebound. When a tracked team posts a <strong>W1 right after a loss streak</strong>, Dakota bets the next game — riding the momentum back the other way. Simple, fast, no record-gating. Each W1-after-L inside the current cycle is a new trigger; the cycle resets when the team strings two-or-more wins together.
          </p>
        </div>
      </div>

      <h2 className="font-display text-3xl tracking-wider mb-4 text-zinc-200">Prediction confidence — what the badges mean</h2>
      <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-10">
        <ul className="text-sm space-y-2 text-zinc-300 leading-relaxed">
          <li><span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-sm" style={{ color: "#22C55E", border: "1px solid #22C55E66", background: "#22C55E11" }}>Strong</span> &nbsp; — deep in cycle, pattern firmly established. Highest expected hit-rate.</li>
          <li><span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-sm" style={{ color: "#007AFF", border: "1px solid #007AFF66", background: "#007AFF11" }}>Standard</span> &nbsp; — within the normal trigger range. Bet at your normal size.</li>
          <li><span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-sm" style={{ color: "#F59E0B", border: "1px solid #F59E0B66", background: "#F59E0B11" }}>Watch</span> &nbsp; — outside the historically best triggers. Tag-along only if you&apos;re already up for the week.</li>
        </ul>
      </div>

      <h2 className="font-display text-3xl tracking-wider mb-4 text-zinc-200">FAQ</h2>
      <div className="space-y-3 mb-10">
        <Faq q="Do I have to follow every prediction?" a="No. Many subscribers cherry-pick by analyst or by 'Strong' confidence only. You&apos;ll lose less to variance if you follow every prediction, but the long-run edge is built into both analysts independently — so picking one is fine too." />
        <Faq q="What's the recommended bankroll?" a="20× your base unit is the floor. If your base unit is $50, that&apos;s a $1,000 bankroll. The Optimizer flags you if your base unit creeps above 5% of bankroll — that&apos;s the warn-line; anything above 10% is high-risk and we&apos;ll surface a louder warning." />
        <Faq q="When are picks published?" a="MLB predictions lock at ~1 AM ET (after the previous day's games settle). WNBA predictions lock at ~10 PM PT. Both go out as a single morning email blast around 9 AM ET if you&apos;ve opted in." />
        <Faq q="What happens if both teams in the same game qualify?" a="We skip both. We never bet against ourselves. You&apos;ll see a SKIP-BOTH note in the trigger map on those days — historically rare but it does happen." />
        <Faq q="Can I see every prior prediction?" a="Yes — /history shows all settled predictions, /receipts shows the tamper-evident ledger with lock timestamps. Every result is permanent. We don&apos;t re-grade or delete." />
      </div>

      <div className="bg-gradient-to-r from-[#22C55E]/10 to-[#007AFF]/5 border border-[#22C55E]/40 rounded-sm p-6 text-center">
        <h3 className="font-display text-2xl tracking-wider mb-2">Ready to follow?</h3>
        <p className="text-sm text-zinc-400 mb-4">Start with the Position Sizer — it takes 60 seconds to build your personalized ladder.</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/optimizer" data-testid="gs-cta-optimizer" className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#22D3EE] text-black text-xs uppercase tracking-[0.2em] font-bold rounded-sm hover:bg-cyan-200 transition-colors">
            <Calculator size={14} weight="fill" /> Build my sizer
          </Link>
          <Link to="/sports/mlb" data-testid="gs-cta-picks" className="inline-flex items-center gap-1.5 px-5 py-2.5 border border-white/40 hover:border-white text-xs uppercase tracking-[0.2em] font-bold rounded-sm transition-colors">
            See today&apos;s picks →
          </Link>
        </div>
      </div>
    </div>
  );
}
